import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InspectionCategoryPanel from './components/InspectionCategoryPanel';
import InspectionProgressBar from './components/InspectionProgressBar';

import AutoSaveIndicator from './components/AutoSaveIndicator';
import RecentInspectionsDropdown from './components/RecentInspectionsDropdown';
import InspectionVehicleSelector from './components/InspectionVehicleSelector';
import InspectionAiPanel from './components/InspectionAiPanel';
import { inspectionService } from '../../services/inspectionService';
import { workOrderService } from '../../services/workOrderService';
import { quoteRequestService } from '../../services/quoteRequestService';
import { useAuth } from '../../contexts/AuthContext';
import { withTimeout } from '../../utils/supabaseTimeout';
import { supabase } from '../../lib/supabase';
import NavAcciones from '../../components/NavAcciones';

const TechnicalInspection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('brakes');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState('');
  // View mode: 'edit' (new/current) | 'view' (read-only existing)
  const [viewMode, setViewMode] = useState('edit');
  const [viewedInspection, setViewedInspection] = useState(null);
  const [sendingToSuppliers, setSendingToSuppliers] = useState(false);
  const [supplierSentMsg, setSupplierSentMsg] = useState('');

  // ── Read receptionId from URL query params (new flow from Reception) ──
  const urlSearchParams = new URLSearchParams(location?.search);
  const urlReceptionId = urlSearchParams?.get('receptionId') || null;
  // Legacy: keep workOrderId support for existing links
  const urlWorkOrderId = urlSearchParams?.get('workOrderId') || null;

  // Navigation state from Reception (legacy location.state.flow)
  const fromReception = location?.state?.fromReception || false;
  const receptionVehicleId = location?.state?.vehicleId || null;
  const receptionClientId = location?.state?.clientId || null;
  const receptionVehicleData = location?.state?.vehicleData || null;
  const receptionClientData = location?.state?.clientData || null;
  const receptionMotivoVisita = location?.state?.motivoVisita || '';
  const receptionObservaciones = location?.state?.observaciones || '';
  const receptionOdometer = location?.state?.odometer || '';
  // Legacy: work order id (backward compat)
  const receptionWorkOrderId = location?.state?.workOrderId || null;
  // Reception record id (new flow)
  const receptionId = location?.state?.receptionId || null;

  const workOrderFromState = location?.state?.workOrder;
  const companyId = userProfile?.company_id || userProfile?.companyId || workOrderFromState?.companyId || null;

  // ── Build initial selectedUnit synchronously from Reception state ──
  const buildUnitFromReception = () => {
    if (fromReception && receptionVehicleId && receptionVehicleData) {
      return {
        receptionId: receptionId || receptionWorkOrderId || null,
        vehicleId: receptionVehicleId,
        clientId: receptionClientId,
        folioOT: receptionWorkOrderId ? `OT-${String(receptionWorkOrderId)?.slice(-6)}` : 'Recepción',
        estadoOT: 'recepcionado',
        fechaIngreso: new Date()?.toISOString(),
        make: receptionVehicleData?.marca || '',
        model: receptionVehicleData?.modelo || '',
        year: receptionVehicleData?.year || '',
        vin: receptionVehicleData?.vin || '',
        plates: receptionVehicleData?.placas || '',
        color: receptionVehicleData?.color || '',
        motor: receptionVehicleData?.motor || '',
        combustible: receptionVehicleData?.combustible || '',
        kilometraje: receptionOdometer || '',
        clientName: receptionClientData?.nombre || '',
        motivoVisita: receptionMotivoVisita,
        observaciones: receptionObservaciones,
      };
    }
    return null;
  };

  // --- VEHICLE SELECTOR STATE ---
  const [selectedUnit, setSelectedUnit] = useState(() => buildUnitFromReception());
  const [selectedReceptionId, setSelectedReceptionId] = useState(
    urlReceptionId || urlWorkOrderId || location?.state?.receptionId || location?.state?.workOrderId || null
  );
  const [isLoadingInspection, setIsLoadingInspection] = useState(false);
  const [existingInspectionIds, setExistingInspectionIds] = useState({});
  const [selectorRefreshTrigger, setSelectorRefreshTrigger] = useState(0);
  // --- END VEHICLE SELECTOR STATE ---

  const inspectionCategories = [
    {
      key: 'brakes',
      dbCategory: 'frenos',
      title: 'Sistema de Frenos',
      icon: 'Disc',
      templates: [
        {
          name: 'Desgaste de pastillas',
          findings: 'Pastillas delanteras con 30% de vida útil restante. Pastillas traseras con 50% de vida útil. Discos con desgaste uniforme dentro de especificaciones.',
          recommendations: 'Reemplazar pastillas delanteras en los próximos 5,000 km. Monitorear pastillas traseras. Realizar limpieza y lubricación de calipers.'
        },
        {
          name: 'Fuga de líquido',
          findings: 'Se detecta humedad en manguera de freno trasero izquierdo. Nivel de líquido de frenos por debajo del mínimo. No se observan fugas activas en calipers.',
          recommendations: 'Reemplazar manguera de freno trasero izquierdo. Realizar sangrado completo del sistema. Verificar ausencia de fugas después del servicio.'
        }
      ]
    },
    {
      key: 'suspension',
      dbCategory: 'suspension',
      title: 'Sistema de Suspensión',
      icon: 'Waves',
      templates: [
        {
          name: 'Amortiguadores desgastados',
          findings: 'Amortiguadores delanteros presentan fuga de aceite. Prueba de rebote indica pérdida de capacidad de amortiguación. Amortiguadores traseros en buen estado.',
          recommendations: 'Reemplazar amortiguadores delanteros por par. Realizar alineación y balanceo después del reemplazo. Inspeccionar soportes superiores.'
        },
        {
          name: 'Terminales flojas',
          findings: 'Terminal de dirección derecha con juego excesivo. Rótulas inferiores con holgura. Bujes de barra estabilizadora deteriorados.',
          recommendations: 'Reemplazar terminal de dirección derecha. Cambiar rótulas inferiores. Sustituir bujes de barra estabilizadora. Realizar alineación completa.'
        }
      ]
    },
    {
      key: 'tuning',
      dbCategory: 'motor',
      title: 'Afinación y Motor',
      icon: 'Wrench',
      templates: [
        {
          name: 'Bujías desgastadas',
          findings: 'Bujías con 60,000 km de uso. Electrodos con desgaste considerable. Cables de bujías en buen estado. Bobinas funcionando correctamente.',
          recommendations: 'Reemplazar juego completo de bujías. Limpiar cámara de combustión. Verificar sincronización de encendido. Realizar prueba de compresión.'
        },
        {
          name: 'Filtros saturados',
          findings: 'Filtro de aire con 80% de saturación. Filtro de combustible con 40,000 km de uso. Filtro de aceite próximo a cambio según kilometraje.',
          recommendations: 'Reemplazar filtro de aire. Cambiar filtro de combustible. Programar cambio de aceite y filtro. Limpiar cuerpo de aceleración.'
        }
      ]
    },
    {
      key: 'others',
      dbCategory: 'otros',
      title: 'Otros Sistemas',
      icon: 'Settings',
      templates: [
        {
          name: 'Sistema eléctrico',
          findings: 'Batería con 3 años de antigüedad, voltaje en 12.4V. Alternador generando 13.8V. Luces delanteras con opacidad. Sistema de arranque normal.',
          recommendations: 'Realizar prueba de carga de batería. Pulir y sellar faros delanteros. Verificar conexiones eléctricas. Revisar sistema de carga en próximo servicio.'
        },
        {
          name: 'Neumáticos',
          findings: 'Neumáticos delanteros con 4mm de profundidad. Neumáticos traseros con 6mm. Desgaste irregular en llanta delantera derecha. Presión correcta en todas las llantas.',
          recommendations: 'Reemplazar neumáticos delanteros. Realizar rotación de llantas. Verificar alineación para corregir desgaste irregular. Balanceo de las 4 ruedas.'
        }
      ]
    },
    {
      key: 'tires',
      dbCategory: 'llantas',
      title: 'Llantas',
      icon: 'Circle',
      templates: [
        {
          name: 'Desgaste irregular',
          findings: 'Llanta delantera derecha con desgaste irregular en el hombro interior. Posible problema de alineación o presión incorrecta.',
          recommendations: 'Reemplazar llanta con desgaste irregular. Realizar alineación de 4 ruedas. Verificar presión y alineación.'
        },
        {
          name: 'Baja profundidad',
          findings: 'Llantas delanteras con profundidad de banda de rodamiento de 2mm (mínimo legal 1.6mm). Indicadores de desgaste al nivel de la banda.',
          recommendations: 'Reemplazar llantas delanteras de inmediato. Programar reemplazo de traseras. Verificar presión y alineación.'
        }
      ]
    }
  ];

  const [inspectionData, setInspectionData] = useState({
    brakes: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
    suspension: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
    tuning: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
    others: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
    tires: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null }
  });

  // ── AUTO-POPULATE from Reception state (vehicleId path — no OT needed) ──
  useEffect(() => {
    if (fromReception && receptionVehicleId && receptionVehicleData) {
      const unit = buildUnitFromReception();
      setSelectedUnit(unit);
      setSelectedReceptionId(receptionId || receptionWorkOrderId || receptionVehicleId);
      if (import.meta.env?.DEV) console.log('[INSPECTION] Auto-populated from Reception state:', unit);
    }
  }, [fromReception, receptionVehicleId]);

  // Legacy: Auto-load when navigated from Reception with a workOrderId
  useEffect(() => {
    if (!fromReception && receptionWorkOrderId) {
      localStorage.setItem('last_selected_reception_id', receptionWorkOrderId);
      setSelectorRefreshTrigger(prev => prev + 1);
    }
  }, [receptionWorkOrderId]);

  // --- VEHICLE SELECTION HANDLER (from selector) ---
  const handleVehicleSelected = async (unit) => {
    setSelectedUnit(unit);
    setSelectedReceptionId(unit?.receptionId);
    setIsLoadingInspection(true);
    try {
      if (unit?.receptionId) {
        // Use getByReceptionId for reception flow, getByWorkOrder for legacy
        const existing = unit?.isReceptionFlow
          ? await inspectionService?.getByReceptionId(unit?.receptionId)
          : await inspectionService?.getByWorkOrder(unit?.receptionId);
        if (existing && existing?.length > 0) {
          const newData = {
            brakes: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            suspension: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tuning: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            others: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tires: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null }
          };
          const ids = {};
          const catMap = { frenos: 'brakes', suspension: 'suspension', motor: 'tuning', otros: 'others', llantas: 'tires' };
          existing?.forEach(rec => {
            const key = catMap?.[rec?.category] || rec?.category;
            if (newData?.[key] !== undefined) {
              newData[key] = {
                status: rec?.status || '',
                findings: rec?.findings || '',
                recommendations: rec?.recommendations || '',
                photos: rec?.photos || [],
                refaccion: rec?.refaccion || '',
                cantidad: rec?.cantidad || 1,
                requiereManoObra: rec?.requiereManoObra || false,
                horasEstimadas: rec?.horasEstimadas || null
              };
              ids[key] = rec?.id;
            }
          });
          setInspectionData(newData);
          setExistingInspectionIds(ids);
        } else {
          setInspectionData({
            brakes: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            suspension: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tuning: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            others: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tires: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null }
          });
          setExistingInspectionIds({});
        }
      }
    } catch (err) {
      console.error('[TechnicalInspection] Error loading inspection for unit:', err);
    } finally {
      setIsLoadingInspection(false);
    }
  };
  // --- END VEHICLE SELECTION HANDLER ---

  // Real autosave with debounce
  const autosaveTimerRef = React.useRef(null);
  const lastSavedDataRef = React.useRef(null);

  useEffect(() => {
    if (viewMode !== 'edit') return;
    if (!selectedUnit) return;

    // Debounce: clear previous timer
    if (autosaveTimerRef?.current) clearTimeout(autosaveTimerRef?.current);

    autosaveTimerRef.current = setTimeout(async () => {
      const hasAnyData = Object.values(inspectionData)?.some(d => d?.status || d?.findings);
      if (!hasAnyData) return;

      // Avoid saving if data hasn't changed
      const dataStr = JSON.stringify(inspectionData);
      if (lastSavedDataRef?.current === dataStr) return;

      setIsSaving(true);
      try {
        // Use workOrderId ONLY for legacy flow (urlWorkOrderId); otherwise always use receptionId
        const isLegacyWOFlow = !!urlWorkOrderId;
        const receptionIdForSave = selectedUnit?.receptionId || urlReceptionId || null;
        const workOrderIdForSave = isLegacyWOFlow ? (urlWorkOrderId || null) : null;
        const vehicleId = selectedUnit?.vehicleId || receptionVehicleId || null;
        const clientId = selectedUnit?.clientId || receptionClientId || null;
        const dbInspectionData = {};
        inspectionCategories?.forEach(cat => { dbInspectionData[cat?.dbCategory] = inspectionData?.[cat?.key]; });

        await inspectionService?.saveBatchInspections(workOrderIdForSave, dbInspectionData, vehicleId, clientId, receptionIdForSave);
        lastSavedDataRef.current = dataStr;
        setLastSaved(new Date());
        setSaveError('');
      } catch (err) {
        console.error('[TechnicalInspection] Autosave failed:', err);
      } finally {
        setIsSaving(false);
      }
    }, 2500);

    return () => { if (autosaveTimerRef?.current) clearTimeout(autosaveTimerRef?.current); };
  }, [inspectionData, viewMode, selectedUnit]);

  // ── NEW FLOW: Load work_order from URL ?workOrderId= query param ──
  useEffect(() => {
    if (!urlWorkOrderId) return;
    const isDev = import.meta.env?.DEV;
    if (isDev) console.log('[INSPECTION_LOAD workOrderId]', urlWorkOrderId);

    const loadFromWorkOrder = async () => {
      setIsLoadingInspection(true);
      try {
        const { data: wo, error: woErr } = await supabase
          ?.from('work_orders')
          ?.select(`
            id,
            folio_ot,
            estado_ot,
            fecha_ingreso,
            kilometraje,
            motivo_visita,
            observaciones,
            vehicle_id,
            client_id,
            vehicle:vehicles(id, vin, marca, modelo, year, motor, combustible, placas, color, tipo),
            client:clients(id, nombre, telefono, email)
          `)
          ?.eq('id', urlWorkOrderId)
          ?.maybeSingle();

        if (woErr || !wo) {
          console.error('[INSPECTION_LOAD] work_order not found:', woErr?.message);
          setSaveError('No se encontró la Orden de Trabajo. Verifica el enlace.');
          return;
        }

        if (!wo?.vehicle_id) {
          console.warn('[INSPECTION_LOAD] vehicle_id is null in work_order');
          setSaveError('Falta vehicle_id en la Orden de Trabajo.');
        }

        const unit = {
          receptionId: wo?.id,
          folioOT: wo?.folio_ot || `OT-${String(wo?.id)?.slice(-6)}`,
          estadoOT: wo?.estado_ot || 'recibido',
          fechaIngreso: wo?.fecha_ingreso,
          make: wo?.vehicle?.marca || '',
          model: wo?.vehicle?.modelo || '',
          year: wo?.vehicle?.year || '',
          vin: wo?.vehicle?.vin || '',
          plates: wo?.vehicle?.placas || '',
          color: wo?.vehicle?.color || '',
          motor: wo?.vehicle?.motor || '',
          combustible: wo?.vehicle?.combustible || '',
          kilometraje: wo?.kilometraje || '',
          vehicleId: wo?.vehicle?.id || wo?.vehicle_id || null,
          clientId: wo?.client?.id || wo?.client_id || null,
          clientName: wo?.client?.nombre || '',
          motivoVisita: wo?.motivo_visita || '',
          observaciones: wo?.observaciones || '',
        };

        if (isDev) console.log('[UNIT_LOADED marca modelo vin]', unit?.make, unit?.model, unit?.vin);

        setSelectedUnit(unit);
        setSelectedReceptionId(wo?.id);

        // Load existing inspection data for this work order
        const existing = await inspectionService?.getByWorkOrder(wo?.id);
        if (existing && existing?.length > 0) {
          const newData = {
            brakes: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            suspension: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tuning: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            others: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tires: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null }
          };
          const ids = {};
          const catMap = { frenos: 'brakes', suspension: 'suspension', motor: 'tuning', otros: 'others', llantas: 'tires' };
          existing?.forEach(rec => {
            const key = catMap?.[rec?.category] || rec?.category;
            if (newData?.[key] !== undefined) {
              newData[key] = {
                status: rec?.status || '',
                findings: rec?.findings || '',
                recommendations: rec?.recommendations || '',
                photos: rec?.photos || [],
                refaccion: rec?.refaccion || '',
                cantidad: rec?.cantidad || 1,
                requiereManoObra: rec?.requiereManoObra || false,
                horasEstimadas: rec?.horasEstimadas || null
              };
              ids[key] = rec?.id;
            }
          });
          setInspectionData(newData);
          setExistingInspectionIds(ids);
        }
      } catch (err) {
        console.error('[INSPECTION_LOAD] Error loading work order:', err);
        setSaveError('Error al cargar la Orden de Trabajo.');
      } finally {
        setIsLoadingInspection(false);
      }
    };

    loadFromWorkOrder();
  }, [urlWorkOrderId]);

  // ── NEW FLOW: Load reception from URL ?receptionId= query param ──
  useEffect(() => {
    if (!urlReceptionId) return;
    console.log('[INSPECTION_LOAD receptionId]', urlReceptionId);

    const loadFromReception = async () => {
      setIsLoadingInspection(true);
      try {
        const { data, error: recErr } = await supabase
          ?.from('receptions')
          ?.select(`
            id,
            created_at,
            odometer,
            motivo_visita,
            observaciones,
            vehicles:vehicle_id(id, vin, marca, modelo, year, motor, combustible, placas, color, tipo),
            clients:client_id(id, nombre, telefono, email)
          `)
          ?.eq('id', urlReceptionId)
          ?.single();

        if (recErr || !data) {
          console.error('[INSPECTION_LOAD] reception not found:', recErr?.message);
          setSaveError('No se encontró la recepción. Verifica el enlace.');
          return;
        }

        console.log('[UNIT_LOADED]', data?.vehicles?.vin, data?.vehicles?.marca, data?.vehicles?.modelo);

        const unit = {
          receptionId: data?.id,
          vehicle: data?.vehicles,
          client: data?.clients,
          odometer: data?.odometer,
          motivoVisita: data?.motivo_visita,
          observaciones: data?.observaciones,
          make: data?.vehicles?.marca || '',
          model: data?.vehicles?.modelo || '',
          vin: data?.vehicles?.vin || '',
          year: data?.vehicles?.year || '',
          engine: data?.vehicles?.motor || '',
          motor: data?.vehicles?.motor || '',
          plates: data?.vehicles?.placas || '',
          color: data?.vehicles?.color || '',
          combustible: data?.vehicles?.combustible || '',
          clientName: data?.clients?.nombre || '',
          clientId: data?.clients?.id || null,
          vehicleId: data?.vehicles?.id || null,
          isReceptionFlow: true,
        };

        setSelectedUnit(unit);
        setSelectedReceptionId(data?.id);

        // Load existing inspections for this reception
        const existing = await inspectionService?.getByReceptionId(data?.id);
        if (existing && existing?.length > 0) {
          const newData = {
            brakes: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            suspension: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tuning: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            others: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null },
            tires: { status: '', findings: '', recommendations: '', photos: [], refaccion: '', cantidad: 1, requiereManoObra: false, horasEstimadas: null }
          };
          const ids = {};
          const catMap = { frenos: 'brakes', suspension: 'suspension', motor: 'tuning', otros: 'others', llantas: 'tires' };
          existing?.forEach(rec => {
            const key = catMap?.[rec?.category] || rec?.category;
            if (newData?.[key] !== undefined) {
              newData[key] = {
                status: rec?.status || '',
                findings: rec?.findings || '',
                recommendations: rec?.recommendations || '',
                photos: rec?.photos || [],
                refaccion: rec?.refaccion || '',
                cantidad: rec?.cantidad || 1,
                requiereManoObra: rec?.requiereManoObra || false,
                horasEstimadas: rec?.horasEstimadas || null
              };
              ids[key] = rec?.id;
            }
          });
          setInspectionData(newData);
          setExistingInspectionIds(ids);
        }
      } catch (err) {
        console.error('[INSPECTION_LOAD] Error loading reception:', err);
        setSaveError('Error al cargar la recepción.');
      } finally {
        setIsLoadingInspection(false);
      }
    };

    loadFromReception();
  }, [urlReceptionId]);

  const handleAutoSave = async () => {
    if (!selectedUnit) return;
    setIsSaving(true);
    setSaveError('');
    try {
      // Use workOrderId ONLY for legacy flow (urlWorkOrderId); otherwise always use receptionId
      const isLegacyWOFlow = !!urlWorkOrderId;
      const receptionIdForSave = selectedUnit?.receptionId || urlReceptionId || null;
      const workOrderIdForSave = isLegacyWOFlow ? (urlWorkOrderId || null) : null;
      const vehicleId = selectedUnit?.vehicleId || receptionVehicleId || null;
      const clientId = selectedUnit?.clientId || receptionClientId || null;
      const dbInspectionData = {};
      inspectionCategories?.forEach(cat => { dbInspectionData[cat?.dbCategory] = inspectionData?.[cat?.key]; });

      await inspectionService?.saveBatchInspections(workOrderIdForSave, dbInspectionData, vehicleId, clientId, receptionIdForSave);
      lastSavedDataRef.current = JSON.stringify(inspectionData);
      setLastSaved(new Date());
    } catch (err) {
      console.error('[TechnicalInspection] Manual save failed:', err);
      setSaveError(err?.message || 'Error al guardar. Intente nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCategoryUpdate = (category, data) => {
    setInspectionData(prev => ({
      ...prev,
      [category]: data
    }));
  };

  const handleToggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Handle selecting a recent inspection to VIEW
  const handleSelectRecentInspection = (inspection) => {
    setViewedInspection(inspection);
    setViewMode('view');
    // Scroll to top
    window?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle creating a NEW revision for the same client/vehicle
  const handleNewRevisionFromRecent = (inspection) => {
    const wo = inspection?.work_order;
    // Navigate to vehicle-reception with pre-filled client/vehicle data
    navigate('/vehicle-reception', {
      state: {
        prefillClientId: wo?.client_id,
        prefillVehicleId: wo?.vehicle_id,
        fromInspection: true,
        sourceWorkOrderId: wo?.id,
      }
    });
  };

  // Return to edit mode
  const handleBackToEdit = () => {
    setViewMode('edit');
    setViewedInspection(null);
  };

  const validateInspection = () => {
    const errors = [];
    
    inspectionCategories?.forEach(cat => {
      const data = inspectionData?.[cat?.key];
      if (!data?.status) errors?.push(`${cat?.title}: Falta seleccionar estado`);
      if (!data?.findings) errors?.push(`${cat?.title}: Faltan hallazgos`);
      if (!data?.recommendations) errors?.push(`${cat?.title}: Faltan recomendaciones`);
      if (!data?.photos?.length) errors?.push(`${cat?.title}: Falta evidencia fotográfica`);
    });

    return errors;
  };

  const handleSubmitInspection = () => {
    const errors = validateInspection();
    
    if (errors?.length > 0) {
      alert(`Por favor complete los siguientes campos:\n\n${errors?.join('\n')}`);
      return;
    }

    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
      navigate('/sales-management');
    }, 2000);
  };

  // Handle appointment scheduling from a category panel
  const handleScheduleAppointment = async ({ rubro, rubroLabel, fechaHora, motivo, notas }) => {
    try {
      await inspectionService?.saveAppointment({
        companyId,
        clientId: selectedUnit?.clientId || receptionClientId || null,
        vehicleId: selectedUnit?.vehicleId || receptionVehicleId || null,
        otId: selectedUnit?.receptionId || null,
        rubro: rubroLabel || rubro,
        fechaHora,
        motivo,
        notas
      });
      // Brief success feedback
      setSaveError('');
      alert(`✅ Revisión de ${rubroLabel} agendada correctamente.`);
    } catch (err) {
      console.error('[TechnicalInspection] Schedule appointment failed:', err);
      alert('No se pudo agendar la revisión. Intente nuevamente.');
    }
  };

  const handleFinalizeInspection = async () => {
    const errors = validateInspection();
    
    if (errors?.length > 0) {
      alert(`Por favor complete los siguientes campos:\n\n${errors?.join('\n')}`);
      return;
    }

    setIsSubmitting(true);
    setSaveError('');
    const isDev = import.meta.env?.DEV;

    try {
      // Use workOrderId ONLY for legacy flow (urlWorkOrderId); otherwise always use receptionId
      const isLegacyWOFlow = !!urlWorkOrderId;
      const receptionIdForSave = isLegacyWOFlow ? null : (selectedUnit?.receptionId || urlReceptionId || null);
      const workOrderIdForSave = isLegacyWOFlow ? (urlWorkOrderId || null) : null;
      const vehicleId = selectedUnit?.vehicleId || receptionVehicleId || null;
      const clientId = selectedUnit?.clientId || receptionClientId || null;
      const dbInspectionData = {};
      inspectionCategories?.forEach(cat => { dbInspectionData[cat?.dbCategory] = inspectionData?.[cat?.key]; });

      if (isDev) {
        console.log('[SAVE] Guardando inspección técnica');
        console.log('[SAVE] receptionId:', receptionIdForSave, '| workOrderId:', workOrderIdForSave, '| vehicleId:', vehicleId);
      }

      const savedInspections = await withTimeout(
        inspectionService?.saveBatchInspections(workOrderIdForSave, dbInspectionData, vehicleId, clientId, receptionIdForSave),
        'inspectionService.saveBatchInspections',
        90000
      );

      // Only update work_order status for legacy flow
      if (workOrderIdForSave) {
        if (isDev) console.log('[SAVE] Actualizando estado OT → inspeccionado');
        await withTimeout(
          workOrderService?.updateStatus(workOrderIdForSave, 'inspeccionado'),
          'workOrderService.updateStatus',
          15000
        )?.catch(err => console.warn('[SAVE] updateStatus non-blocking:', err?.message));
      }

      // Sync bad-condition findings to quotation (idempotent)
      try {
        await inspectionService?.syncInspectionToQuote(
          workOrderIdForSave, dbInspectionData, vehicleId, clientId, companyId
        );
        if (isDev) console.log('[SAVE] syncInspectionToQuote completado');
      } catch (syncErr) {
        console.error('[TechnicalInspection] syncInspectionToQuote failed (non-blocking):', syncErr);
      }

      // Upsert outbox_supplier_requests for bad-condition findings
      try {
        await inspectionService?.upsertOutboxSupplierRequest({
          otId: workOrderIdForSave,
          vehicleId,
          clientId,
          companyId,
          vehicleData: selectedUnit || receptionVehicleData,
          inspectionDataByCategory: dbInspectionData
        });
        if (isDev) console.log('[SAVE] outbox_supplier_requests actualizado');
      } catch (outboxErr) {
        console.error('[TechnicalInspection] outbox upsert failed (non-blocking):', outboxErr);
      }

      // Legacy quote request
      try {
        const firstInspectionId = savedInspections?.[0]?.id || null;
        quoteRequestService?.createFromInspection({
          workOrderId: workOrderIdForSave, inspectionId: firstInspectionId, companyId,
          clientId, vehicleId,
          inspectionDataByCategory: dbInspectionData
        })?.catch(qrErr => {
          console.error('[TechnicalInspection] Quote request creation failed (non-blocking):', qrErr);
        });
        if (isDev) console.log('[SAVE] Quote request iniciado en background');
      } catch (qrErr) {
        console.error('[TechnicalInspection] Quote request creation failed (non-blocking):', qrErr);
      }

      const inspectionFindings = inspectionCategories?.map(cat => ({
        category: cat?.title, dbCategory: cat?.dbCategory,
        status: inspectionData?.[cat?.key]?.status, findings: inspectionData?.[cat?.key]?.findings,
        recommendations: inspectionData?.[cat?.key]?.recommendations,
        parts: inspectionData?.[cat?.key]?.parts || [], photos: inspectionData?.[cat?.key]?.photos || []
      }));

      const navWorkOrder = selectedUnit
        ? {
            id: selectedUnit?.receptionId,
            receptionId: receptionIdForSave,
            folioOT: selectedUnit?.folioOT,
            vehicle: {
              id: selectedUnit?.vehicleId,
              marca: selectedUnit?.make,
              modelo: selectedUnit?.model,
              year: selectedUnit?.year,
              vin: selectedUnit?.vin,
              placas: selectedUnit?.plates,
              color: selectedUnit?.color,
              motor: selectedUnit?.motor,
              combustible: selectedUnit?.combustible,
            },
            kilometraje: selectedUnit?.kilometraje,
            client: { nombre: selectedUnit?.clientName, id: selectedUnit?.clientId },
            estadoOT: 'inspeccionado'
          }
        : { id: workOrderIdForSave, receptionId: receptionIdForSave, folioOT: null, vehicle: null, client: null, estadoOT: 'inspeccionado' };

      navigate('/quotation-management', {
        state: { workOrder: navWorkOrder, inspectionFindings, receptionId: receptionIdForSave }
      });
    } catch (error) {
      console.error('Error al finalizar inspección:', error);
      const msg = error?.message || 'Error al guardar la inspección. Por favor intente nuevamente.';
      setSaveError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendToSuppliers = async () => {
    if (!selectedUnit) {
      alert('Selecciona una unidad antes de enviar a proveedores.');
      return;
    }
    setSendingToSuppliers(true);
    setSupplierSentMsg('');
    try {
      const workOrderId = selectedUnit?.receptionId || null;
      const vehicleId = selectedUnit?.vehicleId || receptionVehicleId || null;
      const clientId = selectedUnit?.clientId || receptionClientId || null;
      const dbInspectionData = {};
      inspectionCategories?.forEach(cat => { dbInspectionData[cat?.dbCategory] = inspectionData?.[cat?.key]; });

      // Check if there are any bad-condition items to send
      const BAD = new Set(['malo', 'critico', 'requiere_atencion']);
      const hasBadItems = Object.values(dbInspectionData)?.some(d => BAD?.has(d?.status));
      if (!hasBadItems) {
        alert('No hay hallazgos en Mal Estado o Crítico para enviar a proveedores.');
        return;
      }

      await inspectionService?.upsertOutboxSupplierRequest({
        otId: workOrderId,
        vehicleId,
        clientId,
        companyId,
        vehicleData: selectedUnit || receptionVehicleData,
        inspectionDataByCategory: dbInspectionData
      });

      setSupplierSentMsg('✅ Solicitud lista para enviar a Proveedores (refaccionarías y llanteras)');
      setTimeout(() => setSupplierSentMsg(''), 5000);
    } catch (err) {
      console.error('[TechnicalInspection] handleSendToSuppliers failed:', err);
      alert('Error al preparar solicitud para proveedores: ' + (err?.message || 'Error desconocido'));
    } finally {
      setSendingToSuppliers(false);
    }
  };

  // VIEW MODE: show read-only inspection
  if (viewMode === 'view' && viewedInspection) {
    const wo = viewedInspection?.work_order;
    const vehicle = wo?.vehicle;
    const client = wo?.client;
    return (
      <div className="min-h-screen bg-background">
        <MainSidebar isCollapsed={isSidebarCollapsed} userRole={userProfile?.role || 'taller'} />
        <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1">
            <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring"
                >
                  <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                </button>
                <div className="min-w-0 pl-10 lg:pl-0">
                  <h1 className="text-xl font-heading font-bold text-foreground leading-tight">Revisión en Consulta</h1>
                  <p className="text-sm caption text-muted-foreground mt-1">
                    {wo?.folio_ot} · {client?.nombre} · {vehicle?.marca} {vehicle?.modelo} {vehicle?.año}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={handleBackToEdit}>
                  Volver
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => handleNewRevisionFromRecent(viewedInspection)}
                >
                  Nueva Revisión
                </Button>
              </div>
            </div>
          </header>
          <main className="p-4 md:p-6 lg:p-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-xs text-muted-foreground">Folio OT</p>
                  <p className="text-sm font-semibold font-mono">{wo?.folio_ot || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="text-sm font-semibold">{client?.nombre || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vehículo</p>
                  <p className="text-sm font-semibold">{vehicle ? `${vehicle?.marca} ${vehicle?.modelo} ${vehicle?.año}` : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fecha Revisión</p>
                  <p className="text-sm font-semibold">
                    {viewedInspection?.created_at
                      ? new Date(viewedInspection?.created_at)?.toLocaleDateString('es-MX')
                      : '-'}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Categoría</p>
                  <p className="text-sm font-medium">{viewedInspection?.category}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Estado</p>
                  <p className="text-sm font-medium capitalize">{viewedInspection?.status?.replace('_', ' ')}</p>
                </div>
                {viewedInspection?.findings && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Hallazgos</p>
                    <p className="text-sm">{viewedInspection?.findings}</p>
                  </div>
                )}
                {viewedInspection?.recommendations && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recomendaciones</p>
                    <p className="text-sm">{viewedInspection?.recommendations}</p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => handleNewRevisionFromRecent(viewedInspection)}
                >
                  Nueva Revisión para este Vehículo
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} userRole={userProfile?.role || 'taller'} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring flex-shrink-0"
              >
                <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-bold text-foreground leading-tight">
Inspección
                </h1>
                <p className="text-sm caption text-muted-foreground mt-1">
                  Evaluación detallada del vehículo
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <RecentInspectionsDropdown
                onSelectInspection={handleSelectRecentInspection}
                onNewRevision={handleNewRevisionFromRecent}
                currentWorkOrderId={selectedUnit?.receptionId || null}
              />
              <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
          <NavAcciones
            onRegresar={() => navigate(-1)}
            onSalir={async () => { await supabase?.auth?.signOut(); navigate('/signin'); }}
            label="Inspección Técnica"
          />
          {/* Reception info banner — shown when coming from Reception without OT */}
          {fromReception && receptionVehicleData && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-success/10 border border-success/20">
              <Icon name="CheckCircle2" size={16} className="text-success shrink-0" />
              <p className="text-sm text-success font-medium">
                Recepción guardada. Vehículo: {receptionVehicleData?.marca} {receptionVehicleData?.modelo} {receptionVehicleData?.year} · {receptionVehicleData?.vin || receptionVehicleData?.placas}
              </p>
            </div>
          )}

          {/* VEHICLE SELECTOR */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Car" size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">Seleccionar Unidad a Inspeccionar</h2>
              </div>
              {selectedUnit && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/8 border border-primary/20">
                  <Icon name="CheckCircle2" size={14} className="text-primary" />
                  <span className="text-xs font-medium text-primary">
                    {selectedUnit?.make} {selectedUnit?.model} · {selectedUnit?.folioOT}
                  </span>
                  {isLoadingInspection && (
                    <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin ml-1" />
                  )}
                </div>
              )}
            </div>

            {/* Only show selector if NOT coming from reception with vehicle data */}
            {!(fromReception && receptionVehicleData) && (
              <InspectionVehicleSelector
                onVehicleSelected={handleVehicleSelected}
                selectedReceptionId={selectedReceptionId}
                refreshTrigger={selectorRefreshTrigger}
              />
            )}

            {/* When coming from reception, show a compact selector to change unit if needed */}
            {fromReception && receptionVehicleData && !selectedUnit && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    // Allow switching to a different unit
                    setSelectedUnit(null);
                    setSelectorRefreshTrigger(prev => prev + 1);
                  }}
                  className="text-xs text-muted-foreground hover:text-primary underline flex items-center gap-1"
                >
                  <Icon name="RefreshCw" size={11} />
                  Cambiar unidad
                </button>
              </div>
            )}

            {/* Selected unit info card */}
            {selectedUnit && !isLoadingInspection && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 px-4 py-3 bg-muted/30 rounded-lg border border-border/50">
                <div>
                  <p className="text-xs text-muted-foreground">Marca / Modelo</p>
                  <p className="text-sm font-semibold text-foreground">{selectedUnit?.make || '—'} {selectedUnit?.model || ''} {selectedUnit?.year || ''}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">VIN</p>
                  <p className="text-sm font-mono text-foreground">{selectedUnit?.vin || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Placas</p>
                  <p className="text-sm font-mono text-foreground">{selectedUnit?.plates || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Motor</p>
                  <p className="text-sm text-foreground">{selectedUnit?.motor || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Combustible</p>
                  <p className="text-sm text-foreground capitalize">{selectedUnit?.combustible || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Kilometraje</p>
                  <p className="text-sm text-foreground">{selectedUnit?.kilometraje ? `${Number(selectedUnit?.kilometraje)?.toLocaleString('es-MX')} km` : '—'}</p>
                </div>
              </div>
            )}

            {/* Show full selector when user clicks "Cambiar unidad" */}
            {fromReception && receptionVehicleData && !selectedUnit && (
              <InspectionVehicleSelector
                onVehicleSelected={handleVehicleSelected}
                selectedReceptionId={selectedReceptionId}
                refreshTrigger={selectorRefreshTrigger}
              />
            )}

            {!selectedUnit && !(fromReception && receptionVehicleData) && (
              <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={12} />
Selecciona una unidad para iniciar o continuar su inspección.
              </p>
            )}
          </div>

          {/* Motivo de visita — shown when coming from Reception */}
          {fromReception && (receptionMotivoVisita || receptionObservaciones) && (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="MessageSquare" size={16} className="text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Motivo de Visita (desde Recepción)</h3>
              </div>
              {receptionMotivoVisita && (
                <p className="text-sm text-foreground mb-1"><span className="font-medium">Motivo:</span> {receptionMotivoVisita}</p>
              )}
              {receptionObservaciones && (
                <p className="text-sm text-foreground"><span className="font-medium">Observaciones:</span> {receptionObservaciones}</p>
              )}
            </div>
          )}

          <InspectionProgressBar 
            categories={inspectionCategories} 
            inspectionData={inspectionData} 
          />

          {/* Save error banner */}
          {saveError && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Error al guardar</p>
                <p className="text-sm text-destructive/80 mt-0.5">{saveError}</p>
              </div>
              <button type="button" onClick={() => setSaveError('')} className="text-destructive/60 hover:text-destructive">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Supplier sent confirmation banner */}
          {supplierSentMsg && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-success/10 border border-success/30">
              <Icon name="CheckCircle2" size={18} className="text-success shrink-0" />
              <p className="text-sm font-medium text-success">{supplierSentMsg}</p>
            </div>
          )}

          <div className="space-y-4 md:space-y-6">
            {inspectionCategories?.map((category) => (
              <InspectionCategoryPanel
                key={category?.key}
                category={category?.key}
                dbCategory={category?.dbCategory}
                title={category?.title}
                icon={category?.icon}
                data={inspectionData?.[category?.key]}
                onUpdate={handleCategoryUpdate}
                isExpanded={expandedCategory === category?.key}
                onToggle={() => handleToggleCategory(category?.key)}
                companyId={companyId}
                templates={category?.templates}
                onScheduleAppointment={handleScheduleAppointment}
                vehicleInfo={selectedUnit}
              />
            ))}
          </div>

          <InspectionAiPanel
            selectedUnit={selectedUnit}
            inspectionData={inspectionData}
            workOrderId={selectedUnit?.receptionId || receptionWorkOrderId || null}
            companyId={companyId}
          />

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-end">
            {!selectedUnit && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground px-3 py-2 rounded-lg bg-muted/50 border border-border/50">
                <Icon name="AlertCircle" size={15} className="text-warning" />
                <span>Selecciona una unidad para guardar la inspección</span>
              </div>
            )}
            <Button
              variant="outline"
              iconName="Save"
              iconPosition="left"
              onClick={handleAutoSave}
              disabled={isSubmitting || !selectedUnit}
            >
              Guardar Borrador
            </Button>
            <Button
              variant="outline"
              iconName="Send"
              iconPosition="left"
              onClick={handleSendToSuppliers}
              disabled={sendingToSuppliers || isSubmitting || !selectedUnit}
              className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950/20"
            >
              {sendingToSuppliers ? 'Enviando...' : 'Enviar a Proveedores'}
            </Button>
            <Button
              variant="default"
              iconName="Send"
              iconPosition="right"
              onClick={handleFinalizeInspection}
              disabled={isSubmitting || !selectedUnit}
            >
              {isSubmitting ? 'Procesando...' : 'Finalizar inspección y enviar a Cotización'}
            </Button>
          </div>
        </main>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-elevation-5 p-6 md:p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle2" size={40} className="text-success" />
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
              ¡Inspección Completada!
            </h2>
            <p className="text-sm md:text-base caption text-muted-foreground mb-4">
              La orden de trabajo ha sido enviada al departamento de ventas para cotización
            </p>
            <div className="flex items-center justify-center gap-2 text-sm caption text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Redirigiendo...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicalInspection;